import { useState, useEffect, useCallback, useRef } from "react";
import { soundManager } from "./sound";

// Check if current device is strictly a desktop
export const isDesktopEnvironment = (): boolean => {
  if (typeof window === "undefined") return false;
  
  // Exclude touch-first mobile and tablet devices
  const isTouchDevice =
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0;
  
  const isMobileUA =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  // If mobile user agent, strictly not desktop
  if (isMobileUA) return false;

  // On touch screens with screen width under 1024, treat as mobile/tablet
  if (isTouchDevice && window.innerWidth < 1024) return false;

  // Must have minimum desktop viewport width
  return window.innerWidth >= 1024;
};

// Check if Document Picture-in-Picture API is supported on this browser & device
export const isDocumentPiPSupported = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    "documentPictureInPicture" in window &&
    isDesktopEnvironment()
  );
};

export interface UseDocumentPiPOptions {
  width?: number;
  height?: number;
  title?: string;
  onOpen?: () => void;
  onClose?: () => void;
}

export function useDocumentPiP(options: UseDocumentPiPOptions = {}) {
  const {
    width = 400,
    height = 650,
    title = "Pişti Online - Mini Masa",
    onOpen,
    onClose,
  } = options;

  const [isPiPActive, setIsPiPActive] = useState<boolean>(false);
  const [pipContainer, setPipContainer] = useState<HTMLElement | null>(null);
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const pipWindowRef = useRef<Window | null>(null);

  // Check support on mount and window resize
  useEffect(() => {
    const checkSupport = () => {
      setIsSupported(isDocumentPiPSupported());
    };

    checkSupport();
    window.addEventListener("resize", checkSupport);
    return () => window.removeEventListener("resize", checkSupport);
  }, []);

  // Copy stylesheets from main document to PiP window efficiently without duplicate parsing
  const copyStylesToPiP = useCallback((pipWin: Window) => {
    try {
      // Copy all <link rel="stylesheet"> and <style> elements directly
      const styleElements = document.querySelectorAll('link[rel="stylesheet"], style');
      styleElements.forEach((node) => {
        pipWin.document.head.appendChild(node.cloneNode(true));
      });
    } catch (err) {
      console.warn("Could not copy stylesheets to PiP window:", err);
    }
  }, []);

  const closePiP = useCallback(() => {
    if (pipWindowRef.current) {
      try {
        pipWindowRef.current.close();
      } catch (err) {
        console.warn("Error closing PiP window:", err);
      }
      pipWindowRef.current = null;
    }
    setIsPiPActive(false);
    setPipContainer(null);
    onClose?.();
  }, [onClose]);

  const openPiP = useCallback(async (opts?: { width?: number; height?: number; title?: string }) => {
    if (!isDocumentPiPSupported()) {
      return false;
    }

    // If already active, close existing PiP window first
    if (pipWindowRef.current) {
      closePiP();
      return false;
    }

    try {
      const docPiP = (window as any).documentPictureInPicture;
      if (!docPiP || typeof docPiP.requestWindow !== "function") {
        return false;
      }

      // Request Picture-in-Picture window with portrait aspect ratio suited for mobile/desktop card games
      const pipWin = await docPiP.requestWindow({
        width: opts?.width || width,
        height: opts?.height || height,
      });

      pipWindowRef.current = pipWin;

      // Copy styles & fonts
      copyStylesToPiP(pipWin);

      // Set window title and favicon
      pipWin.document.title = opts?.title || title;
      
      // Add background and body formatting
      pipWin.document.body.className =
        "bg-slate-950 text-white overflow-hidden m-0 p-0 w-full h-full select-none";
      pipWin.document.documentElement.className =
        "w-full h-full overflow-hidden bg-slate-950";

      // Create main mount point for React portal
      const container = pipWin.document.createElement("div");
      container.id = "pip-portal-root";
      container.className =
        "w-full h-full relative overflow-y-auto overflow-x-hidden flex flex-col";
      pipWin.document.body.appendChild(container);

      setPipContainer(container);
      setIsPiPActive(true);
      onOpen?.();

      // Ensure audio context remains active on user interaction inside PiP window
      pipWin.addEventListener("pointerdown", () => {
        soundManager.resume();
      }, { passive: true });

      // Listen for window close (user clicked X on PiP window or navigated away)
      pipWin.addEventListener("pagehide", () => {
        setIsPiPActive(false);
        setPipContainer(null);
        pipWindowRef.current = null;
        onClose?.();
      });

      return true;
    } catch (err) {
      console.warn("Failed to open Document Picture-in-Picture window:", err);
      setIsPiPActive(false);
      setPipContainer(null);
      pipWindowRef.current = null;
      return false;
    }
  }, [width, height, title, copyStylesToPiP, closePiP, onOpen, onClose]);

  const togglePiP = useCallback(async (opts?: { width?: number; height?: number; title?: string }) => {
    if (isPiPActive) {
      closePiP();
    } else {
      await openPiP(opts);
    }
  }, [isPiPActive, closePiP, openPiP]);

  const getWindow = useCallback(() => pipWindowRef.current || window, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pipWindowRef.current) {
        try {
          pipWindowRef.current.close();
        } catch { /* ignore */ }
      }
    };
  }, []);

  return {
    isSupported,
    isPiPActive,
    pipContainer,
    pipWindow: pipWindowRef.current,
    getWindow,
    openPiP,
    closePiP,
    togglePiP,
  };
}
